import React, { useEffect, useMemo, useState } from "react";
import axios from "../../API/axios";
import CloseSharpIcon from "@mui/icons-material/CloseSharp";
import { useTranslation } from "react-i18next";
import { Portal } from "../../components/Portal";

const DEFAULT_DIMS = [
  { key: "Skills", label: "Skills", dimensionIndex: 1, weight: 0.3, mandatory: true },
  { key: "Education", label: "Education", dimensionIndex: 2, weight: 0.15, mandatory: false },
  { key: "Languages", label: "Languages", dimensionIndex: 3, weight: 0.1, mandatory: false },
  { key: "Experience", label: "Experience", dimensionIndex: 4, weight: 0.2, mandatory: true },
  { key: "Location", label: "Location", dimensionIndex: 5, weight: 0.1, mandatory: false },
  { key: "Industry", label: "Industry", dimensionIndex: 6, weight: 0.1, mandatory: false },
  { key: "SalaryFit", label: "Salary fit", dimensionIndex: 7, weight: 0.05, mandatory: false },
];

/**
 * Job match weights modal — Skills, Education, Languages, Experience, Location, Industry, Salary fit.
 * Auto-normalizes to 1.00 on save.
 */
const JobMatchWeightsModal = ({ show, jobId, compUid, onClose, onSaved }) => {
  const { t } = useTranslation();
  const sessionId = localStorage.getItem("sessionId");
  const [dimensions, setDimensions] = useState(DEFAULT_DIMS);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [healthIssues, setHealthIssues] = useState([]);
  const [healthy, setHealthy] = useState(true);

  const totalWeight = useMemo(
    () => dimensions.reduce((sum, d) => sum + (Number(d.weight) || 0), 0),
    [dimensions]
  );

  useEffect(() => {
    if (!show || !jobId) return;
    setLoading(true);
    setError("");
    setInfo("");
    axios
      .get(`Match/GetJobMatchWeights?DeviceType=Web&JobId=${jobId}`)
      .then((res) => {
        const data = res?.data?.responseData;
        const dims = data?.dimensions || data?.Dimensions;
        setHealthy(data?.isHealthy ?? data?.IsHealthy ?? true);
        setHealthIssues(data?.healthIssues || data?.HealthIssues || []);
        if (Array.isArray(dims) && dims.length) {
          setDimensions(
            dims.map((d, i) => ({
              key: d.key || d.Key,
              label: d.label || d.Label,
              dimensionIndex: d.dimensionIndex || d.DimensionIndex || i + 1,
              weight: Number(d.weight ?? d.Weight ?? 0),
              mandatory: Boolean(d.mandatory ?? d.Mandatory),
            }))
          );
        } else {
          setDimensions(DEFAULT_DIMS);
        }
      })
      .catch(() => setError("Failed to load match weights."))
      .finally(() => setLoading(false));
  }, [show, jobId]);

  const updateDim = (key, patch) => {
    setDimensions((prev) => prev.map((d) => (d.key === key ? { ...d, ...patch } : d)));
  };

  const handleSave = async () => {
    const mandatoryZero = dimensions.some((d) => d.mandatory && !(Number(d.weight) > 0));
    if (mandatoryZero) {
      setError("Mandatory dimensions must have a weight greater than 0.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const res = await axios.post("Match/SetJobMatchWeights", {
        deviceType: "Web",
        sessionId,
        jobId,
        dimensions,
      });
      const data = res?.data?.responseData;
      if (data?.dimensions || data?.Dimensions) {
        const dims = data.dimensions || data.Dimensions;
        setDimensions(
          dims.map((d, i) => ({
            key: d.key || d.Key,
            label: d.label || d.Label,
            dimensionIndex: d.dimensionIndex || d.DimensionIndex || i + 1,
            weight: Number(d.weight ?? d.Weight ?? 0),
            mandatory: Boolean(d.mandatory ?? d.Mandatory),
          }))
        );
      }
      setHealthy(data?.isHealthy ?? data?.IsHealthy ?? true);
      setHealthIssues(data?.healthIssues || data?.HealthIssues || []);
      onSaved?.(data);
      onClose?.();
    } catch (e) {
      setError(e?.response?.data?.message || "Failed to save match weights.");
    } finally {
      setSaving(false);
    }
  };

  const applyCompanyTemplate = async () => {
    if (!compUid) {
      setError("Company is required to apply a weight template.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const res = await axios.post("Match/ApplyMatchWeightTemplate", {
        deviceType: "Web",
        sessionId,
        compUid,
        jobIds: jobId ? [jobId] : null,
        onlyMissing: false,
      });
      setInfo(res?.data?.message || "Company template applied.");
      const reload = await axios.get(`Match/GetJobMatchWeights?DeviceType=Web&JobId=${jobId}`);
      const data = reload?.data?.responseData;
      const dims = data?.dimensions || data?.Dimensions;
      if (Array.isArray(dims) && dims.length) {
        setDimensions(
          dims.map((d, i) => ({
            key: d.key || d.Key,
            label: d.label || d.Label,
            dimensionIndex: d.dimensionIndex || d.DimensionIndex || i + 1,
            weight: Number(d.weight ?? d.Weight ?? 0),
            mandatory: Boolean(d.mandatory ?? d.Mandatory),
          }))
        );
      }
      setHealthy(data?.isHealthy ?? data?.IsHealthy ?? true);
      setHealthIssues(data?.healthIssues || data?.HealthIssues || []);
      onSaved?.(data);
    } catch (e) {
      setError(e?.response?.data?.message || "Failed to apply company template.");
    } finally {
      setSaving(false);
    }
  };

  const saveAsCompanyTemplate = async () => {
    if (!compUid) {
      setError("Company is required to save a weight template.");
      return;
    }
    const mandatoryZero = dimensions.some((d) => d.mandatory && !(Number(d.weight) > 0));
    if (mandatoryZero) {
      setError("Mandatory dimensions must have a weight greater than 0.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const res = await axios.post("Match/SetCompanyMatchWeights", {
        deviceType: "Web",
        sessionId,
        compUid,
        dimensions,
      });
      setInfo(res?.data?.message || "Saved as company template.");
    } catch (e) {
      setError(e?.response?.data?.message || "Failed to save company template.");
    } finally {
      setSaving(false);
    }
  };

  if (!show) return null;

  return (
    <Portal layer="modal">
    <div className="modal fade show" style={{ display: "block" }} tabIndex="-1">
      <div className="modal-dialog modal-dialog-centered" role="document">
        <div className="modal-content p-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h5 className="mb-0">{t("Common.JobMatchWeights")}</h5>
            <CloseSharpIcon className="pointer" onClick={onClose} />
          </div>
          <p className="text-muted small mb-2">
            Weights control candidate / job scoring. Mandatory gate = candidate must pass that dimension.
            Total auto-normalizes to 1.00 on save.
          </p>
          <div className="wlj-weight-total">
            Total weight: <strong>{totalWeight.toFixed(2)}</strong>
            {Math.abs(totalWeight - 1) > 0.001
              ? " — Will auto-normalize to 1.00 on save for accurate scoring."
              : " — Already normalized."}
          </div>
          {(!healthy || healthIssues.length > 0 || dimensions.some((d) => d.mandatory && !(Number(d.weight) > 0))) && (
            <div className="alert alert-danger py-2 small">
              {(healthIssues.length
                ? healthIssues
                : ["Mandatory dimensions must have a weight greater than 0 and the total should be 100%."]
              ).join(" ")}
            </div>
          )}
          {info && <div className="alert alert-success py-2 small">{info}</div>}

          {loading ? (
            <p>Loading…</p>
          ) : (
            <div className="d-flex flex-column gap-2">
              {dimensions.map((d) => (
                <div key={d.key} className="d-flex align-items-center justify-content-between gap-2">
                  <div style={{ minWidth: 120 }}>
                    <div className="fw-semibold">{d.label}</div>
                    <div className="text-muted small">Dimension {d.dimensionIndex}</div>
                  </div>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className="form-control"
                    style={{ maxWidth: 100 }}
                    value={d.weight}
                    onChange={(e) => updateDim(d.key, { weight: Number(e.target.value) })}
                  />
                  <label className="d-flex align-items-center gap-1 mb-0 small">
                    <input
                      type="checkbox"
                      checked={d.mandatory}
                      onChange={(e) => updateDim(d.key, { mandatory: e.target.checked })}
                    />
                    Mandatory
                  </label>
                </div>
              ))}
            </div>
          )}

          {error && <p className="text-danger small mt-2 mb-0">{error}</p>}

          <div className="d-flex flex-wrap justify-content-end gap-2 mt-3">
            <button type="button" className="wlj-ghost-btn" onClick={onClose}>
              CLOSE
            </button>
            {compUid && (
              <>
                <button
                  type="button"
                  className="wlj-ghost-btn"
                  disabled={saving || loading}
                  onClick={applyCompanyTemplate}
                >
                  Apply company template
                </button>
                <button
                  type="button"
                  className="wlj-ghost-btn"
                  disabled={saving || loading}
                  onClick={saveAsCompanyTemplate}
                >
                  Save as company template
                </button>
              </>
            )}
            <button type="button" className="mainBtn1" disabled={saving || loading} onClick={handleSave}>
              {saving ? "SAVING…" : "SAVE"}
            </button>
          </div>
        </div>
      </div>
    </div>
    </Portal>
  );
};

export default JobMatchWeightsModal;
